/**
 * SlicePlaneManager - Manages collection of SlicePlanes
 *
 * Handles multiple slice planes for a chair design, including:
 * - Plane collection management
 * - Cross-slice intersection detection
 * - Slot generation for assembly
 * - Coordinate transformations
 *
 * This is the core of the waffle construction system where
 * multiple planes intersect and require slots for assembly.
 */

import { SlicePlane } from './SlicePlane.js';
import { SliceSlot } from './SliceSlot.js';
import { Vec2D } from '../sketch/geometry/Vec2D.js';
import * as THREE from 'three';
import * as GeometryOperations from './GeometryOperations.js';
import { CrossSliceSelection } from './CrossSliceSelection.js';
import { CapType } from './CapType.js';
import { unionShapes, sampleRing } from './PolygonOps.js';

// Debug visualization flag (controlled by VITE_DEBUG_INTERSECTIONS env variable)
const DEBUG_INTERSECTIONS = (import.meta.env?.VITE_DEBUG_INTERSECTIONS ?? globalThis.VITE_DEBUG_INTERSECTIONS) === 'true';

export class SlicePlaneManager {
    /**
     * Create a new SlicePlaneManager
     * @param {SceneManager} sceneManager - Optional scene manager for debug visualization
     */
    constructor(sceneManager = null) {
        // Collection of slice planes
        this.planes = [];

        // Selected plane (for editing)
        this.selectedPlane = null;

        // Intersection cache
        this.intersections = new Map(); // key: "planeId1-planeId2", value: intersection data

        // Slots generated at intersections
        this.slots = []; // Array of SliceSlot objects

        // Settings
        this.autoGenerateSlots = true;
        this.slotWidth = 5.0; // Width of assembly slots in mm
        this.slotLength = 10.0; // Default length of slots in mm

        // Base planes (from Application, at different Z levels)
        this.basePlanes = [];

        // Debug visualization
        this.sceneManager = sceneManager;
        this.debugSpheres = []; // Array of debug sphere meshes
    }

    /**
     * Set base planes to check for intersections at different Z levels
     * @param {Array<SlicePlane>} basePlanes - Array of base planes
     */
    setBasePlanes(basePlanes) {
        this.basePlanes = basePlanes;
        console.log(`📐 SlicePlaneManager: Set ${basePlanes.length} base planes for intersection checks`);
    }

    /**
     * Add a slice plane
     * @param {SlicePlane} plane - Plane to add
     */
    addPlane(plane) {
        this.planes.push(plane);
        this.updateIntersections();
    }

    /**
     * Remove a slice plane
     * @param {SlicePlane} plane - Plane to remove
     */
    removePlane(plane) {
        const index = this.planes.indexOf(plane);
        if (index !== -1) {
            this.planes.splice(index, 1);
            this.updateIntersections();
        }
    }

    /**
     * Get plane by ID
     * @param {number} id - Plane ID
     * @returns {SlicePlane|null} Plane or null if not found
     */
    getPlaneById(id) {
        return this.planes.find(p => p.id === id) || null;
    }

    /**
     * Select a plane
     * @param {SlicePlane} plane - Plane to select
     */
    selectPlane(plane) {
        // Unselect all planes
        this.planes.forEach(p => p.unselect());

        // Select the target plane
        if (plane) {
            plane.select();
            this.selectedPlane = plane;
        } else {
            this.selectedPlane = null;
        }
    }

    /**
     * Get all intersections for a specific plane
     * @param {SlicePlane} plane - Plane to check
     * @returns {Array} Array of intersection data
     */
    getIntersectionsForPlane(plane) {
        const intersections = [];

        for (const [key, intersection] of this.intersections) {
            const [id1, id2] = key.split('-').map(Number);
            if (id1 === plane.id || id2 === plane.id) {
                intersections.push(intersection);
            }
        }

        return intersections;
    }

    /**
     * Update all intersection calculations
     * Called when planes are added/removed/modified
     */
    updateIntersections() {
        // Clear existing intersections
        this.intersections.clear();

        if (DEBUG_INTERSECTIONS) {
            console.log(`🔍 Checking intersections between ${this.planes.length} planes...`);
        }

        // Check all plane pairs
        for (let i = 0; i < this.planes.length; i++) {
            for (let j = i + 1; j < this.planes.length; j++) {
                const plane1 = this.planes[i];
                const plane2 = this.planes[j];

                const intersection = this.calculateIntersection(plane1, plane2);

                if (intersection) {
                    const key = `${plane1.id}-${plane2.id}`;
                    this.intersections.set(key, intersection);
                    if (DEBUG_INTERSECTIONS) {
                        console.log(`   ✓ Intersection found: Plane ${i+1} ↔ Plane ${j+1}`);
                    }
                } else {
                    if (DEBUG_INTERSECTIONS) {
                        console.log(`   ✗ No intersection: Plane ${i+1} ↔ Plane ${j+1}`);
                    }
                }
            }
        }

        if (DEBUG_INTERSECTIONS) {
            console.log(`✅ Updated intersections: ${this.intersections.size} found`);
        }

        // Generate slots at intersections
        if (this.autoGenerateSlots) {
            this.generateSlots();
        }
    }

    /**
     * Calculate intersection between two planes
     * @param {SlicePlane} plane1 - First plane
     * @param {SlicePlane} plane2 - Second plane
     * @returns {Object|null} Intersection data or null if planes don't intersect
     */
    calculateIntersection(plane1, plane2) {
        // Get the intersection line between the two planes
        const intersectionLine = plane1.getIntersectionLine(plane2);

        if (!intersectionLine) {
            // Planes are parallel
            if (DEBUG_INTERSECTIONS) {
                console.log(`      → Planes are parallel (no intersection line)`);
            }
            return null;
        }

        // Calculate intersection points with each sketch
        const points1 = this.getIntersectionPoints(plane1, intersectionLine);
        const points2 = this.getIntersectionPoints(plane2, intersectionLine);

        if (DEBUG_INTERSECTIONS) {
            console.log(`      → Intersection line found`);
            console.log(`      → Line start: (${intersectionLine.start.x.toFixed(3)}, ${intersectionLine.start.y.toFixed(3)}, ${intersectionLine.start.z.toFixed(3)})`);
            console.log(`      → Line end:   (${intersectionLine.end.x.toFixed(3)}, ${intersectionLine.end.y.toFixed(3)}, ${intersectionLine.end.z.toFixed(3)})`);
            console.log(`      → Plane1 position: (${plane1.position.x.toFixed(3)}, ${plane1.position.y.toFixed(3)}, ${plane1.position.z.toFixed(3)})`);
            console.log(`      → Plane1 normal:   (${plane1.normal.x.toFixed(3)}, ${plane1.normal.y.toFixed(3)}, ${plane1.normal.z.toFixed(3)})`);
            console.log(`      → Plane2 position: (${plane2.position.x.toFixed(3)}, ${plane2.position.y.toFixed(3)}, ${plane2.position.z.toFixed(3)})`);
            console.log(`      → Plane2 normal:   (${plane2.normal.x.toFixed(3)}, ${plane2.normal.y.toFixed(3)}, ${plane2.normal.z.toFixed(3)})`);
            console.log(`      → Points on plane1: ${points1.length}`);
            console.log(`      → Points on plane2: ${points2.length}`);
        }

        if (points1.length === 0 || points2.length === 0) {
            // Intersection line doesn't pass through sketches
            if (DEBUG_INTERSECTIONS) {
                console.log(`      → Intersection line doesn't pass through both sketches`);
            }
            return null;
        }

        return {
            plane1: plane1,
            plane2: plane2,
            line: intersectionLine,
            pointsOnPlane1: points1,
            pointsOnPlane2: points2
        };
    }

    /**
     * Get intersection points between a plane's sketch and a 3D line
     * @param {SlicePlane} plane - Slice plane with sketch
     * @param {THREE.Line3} line - 3D intersection line
     * @returns {Array} Array of {worldPos, planePos, percent} objects
     */
    getIntersectionPoints(plane, line) {
        const points = [];

        if (!plane.sketch || plane.sketch.closedShapes.length === 0) {
            if (DEBUG_INTERSECTIONS) {
                console.log(`      → Plane has no sketch or closedShapes`);
            }
            return points;
        }

        if (DEBUG_INTERSECTIONS) {
            console.log(`      → Checking ${plane.sketch.closedShapes.length} shapes...`);
        }

        // For each closed shape on the plane
        plane.sketch.closedShapes.forEach((shape, shapeIdx) => {
            if (DEBUG_INTERSECTIONS) {
                console.log(`        → Shape ${shapeIdx + 1}: ${shape.points.length} points`);

                // Log first point transformation as example
                if (shape.points.length > 0) {
                    const p1 = shape.points[0];
                    const wp1 = plane.getWorldPos(p1);
                    console.log(`          → Point 0: local(${p1.x.toFixed(2)}, ${p1.y.toFixed(2)}) → world(${wp1.x.toFixed(3)}, ${wp1.y.toFixed(3)}, ${wp1.z.toFixed(3)})`);
                    console.log(`          → Plane position: (${plane.position.x.toFixed(3)}, ${plane.position.y.toFixed(3)}, ${plane.position.z.toFixed(3)})`);
                    console.log(`          → Plane normal: (${plane.normal.x.toFixed(3)}, ${plane.normal.y.toFixed(3)}, ${plane.normal.z.toFixed(3)})`);
                }
            }

            // Check each edge of the path. BEZIER-SAMPLE the outline first
            // (same sampler as meshes/exports): walking the raw control
            // points chords across bezier segments, so ribs on a beziered
            // profile stopped intersecting the true curve and came out too
            // long. Straight-line outlines pass through unchanged.
            const walkPts = shape.points.some(pt => pt.containsBezier && pt.containsBezier())
                ? sampleRing(shape.points)
                : shape.points;
            let edgeChecks = 0;
            let closeMatches = 0;

            for (let i = 0; i < walkPts.length; i++) {
                const p1 = walkPts[i];
                const p2 = walkPts[(i + 1) % walkPts.length];

                // Convert to 3D world coordinates
                const wp1 = plane.getWorldPos(p1);
                const wp2 = plane.getWorldPos(p2);

                // Create edge line segment
                const edge = new THREE.Line3(wp1, wp2);

                // Find closest points between edge and intersection line
                const result = this.closestPointsBetweenLines(edge, line);

                edgeChecks++;

                if (result && result.distance < 1.0) { // Increased tolerance for debugging
                    closeMatches++;
                    if (result.distance < 0.01) {
                        // Lines intersect (or are very close)
                        const worldPos = result.point1;
                        const planePos = plane.getPlanePos(worldPos);

                        // Calculate percent along the shape perimeter
                        const percent = i / walkPts.length;

                        points.push({
                            worldPos: worldPos,
                            planePos: planePos,
                            percent: percent,
                            edgeIndex: i
                        });

                        if (DEBUG_INTERSECTIONS) {
                            console.log(`          ✓ INTERSECTION FOUND at edge ${i}, distance: ${result.distance.toFixed(4)}`);
                        }

                        // DEBUG: Add visual sphere at intersection point
                        if (DEBUG_INTERSECTIONS) {
                            this.addDebugSphere(worldPos, 0x00FF00, 0.05); // Green sphere, 5cm radius
                        }
                    }
                }
            }

            if (DEBUG_INTERSECTIONS) {
                console.log(`        → Checked ${edgeChecks} edges, ${closeMatches} within 1.0 unit, ${points.length} intersections found`);
            }
        });

        return points;
    }

    /**
     * Find closest points between two 3D line segments
     * @param {THREE.Line3} line1 - First line
     * @param {THREE.Line3} line2 - Second line
     * @returns {Object|null} {point1, point2, distance} or null
     */
    closestPointsBetweenLines(line1, line2) {
        const p1 = line1.start;
        const p2 = line1.end;
        const p3 = line2.start;
        const p4 = line2.end;

        const d1 = new THREE.Vector3().subVectors(p2, p1);
        const d2 = new THREE.Vector3().subVectors(p4, p3);
        const r = new THREE.Vector3().subVectors(p1, p3);

        const a = d1.dot(d1);
        const b = d1.dot(d2);
        const c = d2.dot(d2);
        const d = d1.dot(r);
        const e = d2.dot(r);

        const denom = a * c - b * b;

        // Check if lines are parallel
        if (Math.abs(denom) < 0.0001) {
            return null;
        }

        const s = (b * e - c * d) / denom;
        const t = (a * e - b * d) / denom;

        // Check if parameters are within line segments
        if (s < 0 || s > 1 || t < 0 || t > 1) {
            return null;
        }

        const point1 = new THREE.Vector3().addVectors(p1, d1.multiplyScalar(s));
        const point2 = new THREE.Vector3().addVectors(p3, d2.multiplyScalar(t));
        const distance = point1.distanceTo(point2);

        return { point1, point2, distance, s, t };
    }

    /**
     * Generate waffle-joint slots between base planes (chair panels) and
     * cross-slice planes (ribs).
     *
     * For each place a cross-slice crosses a base plane's outline, the two
     * parts share a chord segment along their 3D intersection line. Each part
     * gets a slot running ALONG that chord from its own edge to the chord's
     * midpoint (from opposite ends), so the parts slide together:
     *
     *   base panel:  |======>....|   slot from one end to the middle
     *   cross rib:   |....<======|   slot from the other end to the middle
     *
     * Slot positions/directions are expressed in each plane's PROFILE frame
     * (SlicePlane.getProfilePos) so they can be boolean-subtracted directly
     * from the profile geometry at mesh time.
     */
    generateSlots() {
        // Clear existing slots
        this.slots = [];

        const crossPlanes = this.planes.filter(p => p.profileXAxis);

        crossPlanes.forEach(p => p.slots && p.slots.clear());
        this.basePlanes.forEach(p => p.slots && p.slots.clear());

        for (const basePlane of this.basePlanes) {
            // Guide planes shape profiles but have no physical part - no joint
            if (basePlane.guide) continue;
            if (!basePlane.sketch || !basePlane.sketch.closedShapes || basePlane.sketch.closedShapes.length === 0) continue;

            for (const crossPlane of crossPlanes) {
                // Respect the slice group's through-all option: by default
                // slots only join the rib with the shape that generated it
                const sel = crossPlane.crossSliceSelection;
                const clip = (sel && sel.cropToCurrentShape !== false) ? (sel.shapeIndex ?? 0) : null;

                // LEG brace x LEG-OWNING (outer) panel: the joint is along
                // the leg extension, NOT at the seat edge. Both parts span
                // seat-underside -> foot there and the two waffle slots
                // MEET HALFWAY UP the extension:
                // - the PANEL is slotted from the FOOT edge up to the mid
                // - the RIB is slotted from its TOP EDGE at this depth (the
                //   contour band's upper boundary - a real part edge, so
                //   the slot mouth actually opens) down to the same mid.
                //   Anchoring the rib slot at seatX put its mouth INSIDE
                //   the rib material: an enclosed rectangle, not a slot -
                //   the parts could never slide together.
                // Inner panels fall through to the generic seat-chord slots
                // (the brace crosses them like any other rib).
                if (crossPlane.tiedToLeg && crossPlane.legExtension &&
                    this.isOuterPhysicalPlane(basePlane)) {
                    const { seatX, xFoot, halfW } = crossPlane.legExtension;
                    const zPx = (basePlane.position.z - crossPlane.position.z) * 100;

                    // Rib top edge at this depth: min profile x among the
                    // rib outline points within the leg's depth band
                    let ribTopX = seatX;
                    const profPts = crossPlane.sketch?.closedShapes?.[0]?.points;
                    if (profPts && profPts.length) {
                        const band = profPts.filter(p => Math.abs(p.y - zPx) <= (halfW ?? 20) + 4);
                        if (band.length) {
                            ribTopX = Math.min(...band.map(p => p.x));
                        }
                    }

                    const seatW = crossPlane.getProfileWorldPos(new Vec2D(seatX, zPx));
                    const footW = crossPlane.getProfileWorldPos(new Vec2D(xFoot, zPx));
                    const ribTopW = crossPlane.getProfileWorldPos(new Vec2D(ribTopX, zPx));
                    if (seatW && footW && ribTopW) {
                        // The pair MEETS at the midpoint of the extension
                        // (seat underside -> foot), per the leg-slice spec
                        const mid = seatW.clone().add(footW).multiplyScalar(0.5);
                        // flipSide is NOT honoured here: unlike a normal
                        // rib/panel chord (both ends are real edges, so either
                        // orientation interlocks), the leg extension has just
                        // ONE valid cross-lap. The PANEL's only edge along the
                        // extension is the FOOT - its top merges into the seat
                        // body (interior), so a slot mouth can't open there.
                        // Swapping on flip anchored the panel slot at the rib's
                        // top (interior of the panel): an enclosed rectangle
                        // that the edge-clip cull drops, so the layer lost its
                        // long slot. Always slot the panel from the foot and
                        // the rib from its top edge.
                        this.addWaffleSlot(basePlane, footW, mid, crossPlane.thickness);
                        this.addWaffleSlot(crossPlane, ribTopW, mid, basePlane.thickness);
                    }
                    continue;
                }

                // World points where the cross-slice plane crosses this base
                // plane's outline, clipped to the chord local to the slice's
                // guide-path position (the rib only exists at that chord)
                const intersections = this.filterToLocalChord(
                    basePlane.getIntersectionWith(crossPlane, clip),
                    crossPlane,
                    (() => {
                        const w = this.localStrokeWidthAt(sel, crossPlane) || sel?.strokeWidth;
                        return w ? w * 2 * 0.01 : null;
                    })()
                );
                if (intersections.length < 2) continue;

                // Sort along the intersection line direction:
                // (0,0,1) x crossNormal = (-ny, nx, 0)
                const lineDir = new THREE.Vector3(
                    -crossPlane.normal.y,
                    crossPlane.normal.x,
                    0
                ).normalize();

                const sorted = intersections.slice().sort(
                    (a, b) => a.world.dot(lineDir) - b.world.dot(lineDir)
                );

                // Consecutive pairs = chords inside the outline
                for (let k = 0; k + 1 < sorted.length; k += 2) {
                    let entry = sorted[k].world;
                    let exit = sorted[k + 1].world;

                    // Respect the slice group's flip option
                    if (crossPlane.crossSliceSelection && crossPlane.crossSliceSelection.flipSide) {
                        [entry, exit] = [exit, entry];
                    }

                    const mid = entry.clone().add(exit).multiplyScalar(0.5);

                    // Base panel slotted from 'exit' side, rib from 'entry'
                    // side - opposite ends so the parts interlock
                    this.addWaffleSlot(basePlane, exit, mid, crossPlane.thickness);
                    this.addWaffleSlot(crossPlane, entry, mid, basePlane.thickness);
                }
            }
        }

        // Cull slots that clip a corner of their part: a healthy slot's
        // rectangle crosses the part outline exactly twice (its mouth);
        // crossing 3+ times means it nicks an adjacent edge, leaving a
        // fragile sliver in the fabricated part (Java: removeNonPiercing).
        this.cullEdgeClippingSlots([...this.basePlanes, ...crossPlanes]);

        console.log(`Generated ${this.slots.length} waffle slots (${crossPlanes.length} cross-slices x ${this.basePlanes.length} base planes)`);
    }

    /**
     * True when the given base plane is one of the two OUTER physical
     * (non-guide) panels - the layers that own the legs.
     * @param {SlicePlane} basePlane
     * @returns {boolean}
     */
    isOuterPhysicalPlane(basePlane) {
        const physical = (this.basePlanes || []).filter(p => !p.guide);
        if (physical.length === 0) return false;
        const zs = physical.map(p => p.position.z);
        const z = basePlane.position.z;
        const EPS = 1e-6;
        return Math.abs(z - Math.min(...zs)) < EPS || Math.abs(z - Math.max(...zs)) < EPS;
    }

    /**
     * Stroke width at a rib plane's position along its guide spline.
     * Per-point widths (spline.centreOffset) make the width vary along the
     * path; the nearest centre-path sample's radius gives the local value.
     * Falls back to the selection's scalar strokeWidth.
     * @param {CrossSliceSelection} selection
     * @param {SlicePlane} plane - Rib plane (position in world units)
     * @returns {number|null} Local stroke width in sketch px
     */
    localStrokeWidthAt(selection, plane) {
        const spline = selection?.guideSpline;
        if (spline?.sampleCentrePath) {
            try {
                const samples = spline.sampleCentrePath();
                if (samples.length > 0) {
                    const px = plane.position.x * 100;
                    const py = plane.position.y * 100;
                    let best = null, bestD = Infinity;
                    for (const smp of samples) {
                        const d = (smp.x - px) ** 2 + (smp.y - py) ** 2;
                        if (d < bestD) { bestD = d; best = smp; }
                    }
                    if (best) return best.r * 2;
                }
            } catch (_) { /* fall through */ }
        }
        return selection?.strokeWidth || null;
    }

    /**
     * Remove slots whose rectangle crosses their part's outline more than
     * twice (corner-clipping slots that would nick an adjacent edge).
     * @param {Array<SlicePlane>} planes - Planes whose slots to validate
     */
    cullEdgeClippingSlots(planes) {
        const segX = (a, b, c, d) => {
            const ccw = (p, q, r) => (r.y - p.y) * (q.x - p.x) - (q.y - p.y) * (r.x - p.x);
            const d1 = ccw(c, d, a), d2 = ccw(c, d, b);
            const d3 = ccw(a, b, c), d4 = ccw(a, b, d);
            return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
        };

        const crossingsWithRing = (corners, ring) => {
            let count = 0;
            for (let i = 0; i < corners.length; i++) {
                const a = corners[i], b = corners[(i + 1) % corners.length];
                for (let j = 0; j < ring.length; j++) {
                    const c = ring[j], d = ring[(j + 1) % ring.length];
                    if (segX(a, b, c, d)) count++;
                }
            }
            return count;
        };

        for (const plane of planes) {
            if (!plane.slots || !plane.sketch?.closedShapes?.length) continue;
            // Fabricated parts are the UNION of the plane's shapes (see
            // Application.generateMeshesForPlane), so slot crossings must be
            // counted against the unioned outline: near an overlap of two
            // strokes, a healthy slot crosses BOTH per-shape rings (4
            // crossings) but the actual part outline only twice. Union holes
            // (enclosed gaps between overlapping strokes) count as edges too.
            const shapePolygons = plane.sketch.closedShapes
                .map(shape => ({
                    outer: sampleRing((shape.path || shape).points),
                    holes: []
                }))
                .filter(poly => poly.outer.length >= 3);
            const rings = [];
            for (const poly of unionShapes(shapePolygons)) {
                rings.push(poly.outer);
                for (const hole of poly.holes || []) rings.push(hole);
            }

            const kept = [];
            let dropped = 0;
            for (const slot of plane.slots.getAll()) {
                const corners = slot.getOutline(0, slot.length);
                let total = 0;
                for (const ring of rings) {
                    if (ring.length >= 3) total += crossingsWithRing(corners, ring);
                }
                if (total <= 2) {
                    kept.push(slot);
                } else {
                    dropped++;
                    // Also remove from the flat list used for visualization
                    const gi = this.slots.indexOf(slot);
                    if (gi !== -1) this.slots.splice(gi, 1);
                }
            }
            if (dropped > 0) {
                plane.slots.clear();
                kept.forEach(sl => plane.slots.add(sl));
                console.log(`   - Culled ${dropped} corner-clipping slot(s) on plane ${plane.id}`);
            }
        }
    }

    /**
     * Create a single slot on a plane, running from a world-space edge point
     * to the chord midpoint, expressed in the plane's profile frame.
     * @param {SlicePlane} plane - Plane to cut
     * @param {THREE.Vector3} fromWorld - Slot start (on the part's edge)
     * @param {THREE.Vector3} midWorld - Slot end (chord midpoint)
     * @param {number} width - Slot width = intersecting part's thickness (mm)
     */
    addWaffleSlot(plane, fromWorld, midWorld, width) {
        const p0 = plane.getProfilePos(fromWorld);
        const p1 = plane.getProfilePos(midWorld);
        if (!p0 || !p1) return;

        const dir = new Vec2D(p1.x - p0.x, p1.y - p0.y);
        const length = Math.hypot(dir.x, dir.y);
        if (length < 0.001) return;
        dir.normalize();

        // Start slightly OUTSIDE the part edge so the boolean subtraction
        // cleanly opens the slot mouth (avoids coincident-edge slivers).
        const overshoot = 2; // pixels (= mm)
        const start = new Vec2D(p0.x - dir.x * overshoot, p0.y - dir.y * overshoot);

        const slot = new SliceSlot({
            plane: plane,
            position: start,
            direction: dir,
            length: length + overshoot,
            width: width,
            type: SliceSlot.SLOT
        });

        if (plane.slots) plane.slots.add(slot);
        this.slots.push(slot);
    }

    /**
     * Generate slots for all planes using GeometryOperations
     * This is the enhanced version that uses the full slot generation algorithm
     * from GeometryOperations.js
     */
    generateSlotsAdvanced() {
        console.log('Generating advanced slots for all planes...');

        // Clear existing slots from all planes
        this.planes.forEach(plane => {
            if (plane.slots) {
                plane.slots.clear();
            }
        });

        // Use GeometryOperations to generate slots
        GeometryOperations.generateSlots(this.planes);

        // Collect all slots from planes for easy access
        this.slots = [];
        this.planes.forEach(plane => {
            if (plane.slots) {
                this.slots.push(...plane.slots.getAll());
            }
        });

        console.log(`Generated ${this.slots.length} total slots across all planes`);

        return this.slots;
    }

    /**
     * Build 2D slice profiles for all planes
     * This generates the final 2D shapes with caps and slots
     * @param {Object} options - Build options
     * @returns {Array} Array of built slice paths
     */
    buildAllSlices(options = {}) {
        console.log('Building all slice profiles...');

        const builtSlices = [];

        for (const plane of this.planes) {
            if (!plane.crossSliceSelection) {
                console.warn('Plane missing crossSliceSelection, skipping');
                continue;
            }

            try {
                // Build the 2D profile for this plane
                const slicePath = GeometryOperations.buildSlicePath(
                    plane,
                    this.planes,
                    plane.crossSliceSelection,
                    options
                );

                if (slicePath) {
                    // Store the built path on the plane
                    plane.builtPath = slicePath;
                    builtSlices.push({
                        plane: plane,
                        path: slicePath
                    });

                    console.log(`Built slice for plane ${plane.id}: ${slicePath.points.length} points`);
                }
            } catch (error) {
                console.error(`Error building slice for plane ${plane.id}:`, error);
            }
        }

        console.log(`Built ${builtSlices.length} slice profiles`);

        return builtSlices;
    }

    /**
     * Complete workflow: Generate intersections, slots, and build all profiles
     * This is the main method to call after planes are positioned
     * @param {Object} options - Build options
     * @returns {Object} Results with planes, slots, and paths
     */
    generateComplete(options = {}) {
        console.log('Running complete generation workflow...');

        // Step 1: Update intersections
        this.updateIntersections();

        // Step 2: Generate advanced slots
        this.generateSlotsAdvanced();

        // Step 3: Build all slice profiles
        const builtSlices = this.buildAllSlices(options);

        // Step 4: Validate and cleanup slots
        this.planes.forEach(plane => {
            if (plane.slots && plane.sketch) {
                // Find the outline path (first closed shape)
                const outlinePath = plane.sketch.closedShapes[0];
                if (outlinePath) {
                    const stats = plane.slots.cleanup(outlinePath);
                    console.log(`Plane ${plane.id} slot cleanup: ${stats.total} removed`);
                }
            }
        });

        return {
            planes: this.planes,
            slots: this.slots,
            builtSlices: builtSlices,
            intersectionCount: this.intersections.size
        };
    }

    /**
     * Create parallel slices along a guide path
     * Integrates with GeometryOperations.generateSlicesAlongPath
     * @param {CrossSliceSelection} crossSliceSelection - Slice selection config
     * @returns {Array<SlicePlane>} Generated planes
     */
    async generateSlicesAlongPath(crossSliceSelection, shouldAbort = null) {
        if (!crossSliceSelection.path || !crossSliceSelection.plane) {
            console.warn('CrossSliceSelection missing path or plane');
            return [];
        }

        console.log('Generating slices along path...');
        console.log('   - Start:', crossSliceSelection.start);
        console.log('   - End:', crossSliceSelection.end);
        console.log('   - Spacing:', crossSliceSelection.spacing);
        console.log('   - Type:', crossSliceSelection.type);

        // Calculate number of slices based on path length and spacing
        let numSlices;

        // Import SliceType for proper type checking
        const { SliceType } = await import('./SliceType.js');

        if (crossSliceSelection.type === SliceType.SINGLE_SLICE) {
            // Single slice at a specific position
            numSlices = 1;
            console.log('   - Single slice mode');
        } else {
            // Multiple slices - calculate based on spacing
            // Estimate path length
            const pathLength = this.estimatePathLength(crossSliceSelection.path);
            const usableLength = pathLength * (crossSliceSelection.end - crossSliceSelection.start);
            const spacing = crossSliceSelection.spacing > 1 ? crossSliceSelection.spacing : 50;
            numSlices = Math.min(200, Math.max(2, Math.floor(usableLength / spacing)));

            console.log('   - Estimated path length:', pathLength.toFixed(2), 'mm');
            console.log('   - Usable length:', usableLength.toFixed(2), 'mm');
            console.log('   - Calculated numSlices:', numSlices);
        }

        // Use GeometryOperations to generate the planes
        const newPlanes = GeometryOperations.generateSlicesAlongPath(
            crossSliceSelection.path,       // guideShape
            crossSliceSelection,             // crossSliceSelection
            crossSliceSelection.start,       // start
            crossSliceSelection.end,         // end
            numSlices                        // numSlices
        );

        // Build simple rectangular profiles for cross-slices (Phase 1 MVP)
        // This makes them visible while we implement full slot generation
        const { SketchPath } = await import('../sketch/SketchPath.js');
        const { SketchPoint } = await import('../sketch/SketchPoint.js');

        console.log('   - Building intersection-based profiles for cross-slices');

        // Build profiles based on intersections with ALL other planes
        const keptPlanes = [];
        for (let index = 0; index < newPlanes.length; index++) {
            // A newer sketch change may have superseded this run while we
            // were awaiting - stop adding planes for the stale state
            if (shouldAbort && shouldAbort()) {
                console.log('generateSlicesAlongPath aborted (superseded)');
                return [];
            }

            const crossSlicePlane = newPlanes[index];

            // Check for stroke width on the guide path
            const guidePath = crossSliceSelection.path;
            const strokeWidth = guidePath.strokeWidth || guidePath.width || null;
            console.log(`      → Guide path strokeWidth: ${strokeWidth}`);

            const profile = await this.buildCrossSliceProfile(
                crossSlicePlane,
                crossSliceSelection,
                guidePath
            );

            if (profile) {
                // Add profile to plane's sketch (must be 'sketch' not 'sketchData')
                if (!crossSlicePlane.sketch) {
                    crossSlicePlane.sketch = {
                        closedShapes: []
                    };
                }
                crossSlicePlane.sketch.closedShapes.push(profile);

                console.log(`   - Cross-slice ${index + 1}: Profile with ${profile.points.length} points`);
            } else {
                // No intersections with any panel: DROP the rib. The old
                // fallback pushed a width x 600px rectangle, which rendered
                // as a long straight slab spearing through the chair
                // whenever profile building failed (the visible symptom of
                // the degenerate-tangent bug on beziered guide paths).
                console.warn(`   - Cross-slice ${index + 1}: no panel crossings - dropping rib`);
                continue;
            }

            // Drop ribs whose profile is OBLIQUE to the stroke: near a tight
            // bend the slice plane runs almost parallel to the adjacent arm
            // and its material chord runs lengthwise through the corner -
            // a legitimate rib spans about the stroke width, an oblique one
            // is much wider. (Matches designers' expectation: no rib exactly
            // on a sharp corner.)
            // Expected stroke width at THIS rib's position: per-point
            // widths make it vary along the path
            const expectedWidth = this.localStrokeWidthAt(crossSliceSelection, crossSlicePlane);
            if (expectedWidth &&
                crossSliceSelection.cropToCurrentShape !== false &&
                crossSlicePlane.sketch?.closedShapes?.length) {
                // Judge obliqueness by the widest SINGLE-LAYER chord, not
                // the profile bbox: the chair-curve fan spreads the layers'
                // chords apart, widening the bbox of healthy ribs (which
                // then got mass-culled -> gaps along the curve). A truly
                // oblique corner rib has a WIDE CHORD on individual layers.
                const pts = crossSlicePlane.sketch.closedShapes[0].points;
                const xs = pts.map(pp => pp.x);
                const bboxSpan = Math.max(...xs) - Math.min(...xs);
                const span = crossSlicePlane.profileMaxChordPx ?? bboxSpan;
                if (span > expectedWidth * 1.5) {
                    console.log(`   - Cross-slice ${index + 1}: dropped (oblique corner rib, chord ${Math.round(span)}px vs local stroke ${Math.round(expectedWidth)}px)`);
                    continue;
                }
                // Drop SLIVER ribs too: near a round end cap the slice plane
                // clips only the tip of the stroke, leaving chords far
                // narrower than the stroke - waffle slots then cut clean
                // through it and slot subtraction degenerates into a
                // self-intersecting scribble.
                if (span < expectedWidth * 0.4) {
                    console.log(`   - Cross-slice ${index + 1}: dropped (sliver rib, chord ${Math.round(span)}px vs local stroke ${Math.round(expectedWidth)}px)`);
                    continue;
                }
            }

            crossSlicePlane.crossSliceSelection = crossSliceSelection;
            this.addPlane(crossSlicePlane);
            keptPlanes.push(crossSlicePlane);
        }

        // Remove ribs that physically collide with an earlier rib
        // (Java: useSliceCollisionDetection / SlicePlane.checkForCollisions).
        // Near a bend, fanned ribs can cross inside the material - two parts
        // can't occupy the same space, so later colliding ribs are dropped.
        const collisionFree = this.removeCollidingRibs(keptPlanes);

        // Store reference in selection (only the ribs that were kept)
        crossSliceSelection.planes = collisionFree;

        console.log(`Generated ${collisionFree.length} planes along path (${newPlanes.length - keptPlanes.length} dropped at corners, ${keptPlanes.length - collisionFree.length} dropped as colliding)`);

        return collisionFree;
    }

    /**
     * Drop ribs that physically intersect an already-kept rib.
     * Every rib is a vertical plane, so in the sketch (XY) plane it occupies
     * a thick line segment: its profile X extent along profileXAxis, at its
     * material thickness. Two ribs collide when those segments come closer
     * than their combined half-thickness.
     * @param {Array<SlicePlane>} planes - Candidate ribs (in path order)
     * @returns {Array<SlicePlane>} Non-colliding subset
     */
    removeCollidingRibs(planes) {
        const chordOf = (plane) => {
            const pts = plane.sketch?.closedShapes?.[0]?.points;
            if (!pts || pts.length === 0 || !plane.profileXAxis) return null;
            const xs = pts.map(q => q.x);
            const a = plane.position.clone().addScaledVector(plane.profileXAxis, Math.min(...xs) * 0.01);
            const b = plane.position.clone().addScaledVector(plane.profileXAxis, Math.max(...xs) * 0.01);
            return {
                ax: a.x, ay: a.y, bx: b.x, by: b.y,
                halfT: (plane.thickness * 0.01) / 2,
                tiedToLeg: !!plane.tiedToLeg
            };
        };

        // Min distance between two 2D segments
        const segDist = (s1, s2) => {
            const ptSegDist = (px, py, ax, ay, bx, by) => {
                const dx = bx - ax, dy = by - ay;
                const len2 = dx * dx + dy * dy;
                const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
                return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
            };
            const ccw = (ax, ay, bx, by, cx, cy) => (cy - ay) * (bx - ax) - (by - ay) * (cx - ax);
            const d1 = ccw(s2.ax, s2.ay, s2.bx, s2.by, s1.ax, s1.ay);
            const d2 = ccw(s2.ax, s2.ay, s2.bx, s2.by, s1.bx, s1.by);
            const d3 = ccw(s1.ax, s1.ay, s1.bx, s1.by, s2.ax, s2.ay);
            const d4 = ccw(s1.ax, s1.ay, s1.bx, s1.by, s2.bx, s2.by);
            if (((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0))) return 0; // crossing
            return Math.min(
                ptSegDist(s1.ax, s1.ay, s2.ax, s2.ay, s2.bx, s2.by),
                ptSegDist(s1.bx, s1.by, s2.ax, s2.ay, s2.bx, s2.by),
                ptSegDist(s2.ax, s2.ay, s1.ax, s1.ay, s1.bx, s1.by),
                ptSegDist(s2.bx, s2.by, s1.ax, s1.ay, s1.bx, s1.by)
            );
        };

        const kept = [];
        const keptChords = [];

        // Seed with ribs from OTHER slice groups already in the manager so
        // overlapping groups can't stack coincident ribs. LEG brace planes
        // (tiedToLeg) are generated FIRST (Application.autoGenerateCrossSlices)
        // so they are always among these seeds: any regular rib whose chord
        // hits a leg brace's footprint is culled here - leg slices WIN
        // (Java tiedToLeg culling priority). Moving or deleting the leg
        // regenerates everything, so culled ribs reappear automatically.
        for (const existing of this.planes) {
            if (existing.profileXAxis && !planes.includes(existing)) {
                const c = chordOf(existing);
                if (c) keptChords.push(c);
            }
        }

        for (const plane of planes) {
            // Safety: a leg brace must never be the one culled, whichever
            // path fed it through here
            if (plane.tiedToLeg) {
                kept.push(plane);
                const c = chordOf(plane);
                if (c) keptChords.push(c);
                continue;
            }

            const chord = chordOf(plane);
            let collides = false;
            let collidesWithLeg = false;
            if (chord) {
                for (const other of keptChords) {
                    if (segDist(chord, other) < chord.halfT + other.halfT) {
                        collides = true;
                        collidesWithLeg = !!other.tiedToLeg;
                        break;
                    }
                }
            }
            if (collides) {
                this.removePlane(plane);
                console.log(collidesWithLeg
                    ? `   - Rib dropped: overlaps a LEG brace at (${plane.position.x.toFixed(2)}, ${plane.position.y.toFixed(2)}) - leg slices win`
                    : '   - Rib dropped: collides with an earlier rib');
            } else {
                kept.push(plane);
                if (chord) keptChords.push(chord);
            }
        }
        return kept;
    }

    /**
     * Estimate the height of a plane (bounding box height)
     * @param {SlicePlane} plane - Plane to measure
     * @returns {number} Height in mm
     */
    estimatePlaneHeight(plane) {
        if (!plane || !plane.sketchData || !plane.sketchData.closedShapes) {
            return 200; // Default height
        }

        let minY = Infinity;
        let maxY = -Infinity;

        plane.sketchData.closedShapes.forEach(shape => {
            const path = shape.path || shape;
            if (path.points) {
                path.points.forEach(point => {
                    minY = Math.min(minY, point.y);
                    maxY = Math.max(maxY, point.y);
                });
            }
        });

        if (minY === Infinity || maxY === -Infinity) {
            return 200; // Default if no valid points
        }

        return maxY - minY;
    }

    /**
     * Estimate the length of a path for slice calculations
     * @param {Object} pathWrapper - Wrapped path object
     * @returns {number} Estimated length in mm
     */
    estimatePathLength(pathWrapper) {
        if (!pathWrapper || !pathWrapper.path || !pathWrapper.path.points) {
            return 0;
        }

        const points = pathWrapper.path.points;
        let totalLength = 0;

        for (let i = 0; i < points.length - 1; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            totalLength += Math.sqrt(dx * dx + dy * dy);
        }

        // Close the loop if needed
        if (points.length > 2) {
            const first = points[0];
            const last = points[points.length - 1];
            const dx = last.x - first.x;
            const dy = last.y - first.y;
            totalLength += Math.sqrt(dx * dx + dy * dy);
        }

        return totalLength;
    }

    /**
     * From all crossings of an (infinite) slice plane with an outline, keep
     * only the entry/exit chord LOCAL to the slice's spawn position on the
     * guide path. A U- or S-shaped stroke is crossed several times by one
     * plane; without this filter, profiles and slots span from one arm of
     * the shape across the gap to the other (Java: getIntersectionCentre).
     *
     * @param {Array} points - Intersections ({world: THREE.Vector3, ...})
     * @param {SlicePlane} plane - The cross-slice plane (spawned on the guide)
     * @returns {Array} The two points of the local chord (or fewer)
     */
    filterToLocalChord(points, plane, maxSpanWorld = null, refWorld = null) {
        if (points.length < 2) return points;

        // All points lie on the plane's intersection line with the base
        // plane; parameterize along that line
        const lineDir = new THREE.Vector3(-plane.normal.y, plane.normal.x, 0).normalize();
        const sorted = points.slice().sort((a, b) => a.world.dot(lineDir) - b.world.dot(lineDir));
        // Reference point the local chord is chosen nearest to. Normally the
        // plane's own spawn position; a leg plane passes its SEAT anchor
        // instead (see buildCrossSliceProfile) so every layer picks the seat
        // chord, not a stretcher/offset-line the leg happens to pass nearer
        // its midpoint on the outer layers.
        const t0 = (refWorld || plane.position).dot(lineDir);

        let best = sorted.length === 2 ? [sorted[0], sorted[1]] : null;
        if (!best) {
            let bestDist = Infinity;
            for (let k = 0; k + 1 < sorted.length; k += 2) {
                const ta = sorted[k].world.dot(lineDir);
                const tb = sorted[k + 1].world.dot(lineDir);
                const dist = (t0 >= ta && t0 <= tb)
                    ? 0
                    : Math.min(Math.abs(t0 - ta), Math.abs(t0 - tb));
                if (dist < bestDist) {
                    bestDist = dist;
                    best = [sorted[k], sorted[k + 1]];
                }
            }
        }
        if (!best) return sorted.slice(0, 2);

        // At a tight bend the slice plane runs nearly parallel to the
        // adjacent arm of the stroke: the local chord then extends
        // lengthwise through the corner material far beyond the stroke
        // width. Clamp the chord around the spawn point so ribs can't
        // become lengthwise spears.
        if (maxSpanWorld) {
            const ta = best[0].world.dot(lineDir);
            const tb = best[1].world.dot(lineDir);
            const span = tb - ta;
            if (span > maxSpanWorld) {
                const half = maxSpanWorld / 2;
                const centre = Math.min(Math.max(t0, ta + half), tb - half);
                const lerpAt = (t) => {
                    const f = (t - ta) / span;
                    return {
                        ...best[0],
                        world: best[0].world.clone().lerp(best[1].world, f)
                    };
                };
                best = [lerpAt(centre - half), lerpAt(centre + half)];
            }
        }
        return best;
    }

    /**
     * Build cross-slice profile from intersections with other planes
     *
     * This creates the actual 2D profile shape for a cross-slice by:
     * 1. Finding all planes that intersect this cross-slice
     * 2. Getting intersection points from each plane
     * 3. Building a profile that spans between intersection points
     * 4. Using stroke width for brush strokes or intersection extents for polygons
     *
     * @param {SlicePlane} crossSlicePlane - Cross-slice plane to build profile for
     * @param {CrossSliceSelection} crossSliceSelection - Configuration
     * @param {SketchPath} guidePath - Guide path (for stroke width)
     * @returns {SketchPath|null} Built profile or null if failed
     */
    /**
     * "Flush Tops": split each edge point (a panel crossing) into a pair of
     * points offset by +-(panel thickness)/2 along the edge walk direction
     * (the profile depth axis). The two points share the same width (x), so
     * the rib edge is flat exactly where each panel seats on it.
     * Mirrors the Java pLeft/pRight split in GeometryOperations.buildSlicePath.
     * @param {Array<{x,y,thickness}>} pts - Edge points in profile px
     * @returns {Array<{x,y,flushPair}>} Split edge (2x points), tagged with
     *   a flushPair id so smoothing can keep each seat segment straight
     */
    splitEdgeForFlushTops(pts) {
        if (pts.length < 2) return pts;
        // Walk direction along depth: +1 for the left edge (increasing y),
        // -1 for the right edge (walked backward)
        const dir = Math.sign(pts[pts.length - 1].y - pts[0].y) || 1;
        const out = [];
        pts.forEach((p, i) => {
            const half = (p.thickness ?? 5) / 2;
            out.push({ x: p.x, y: p.y - dir * half, flushPair: i });
            out.push({ x: p.x, y: p.y + dir * half, flushPair: i });
        });
        return out;
    }

    /**
     * "Smooth": resample an edge polyline as a Catmull-Rom curve through its
     * points, inserting 3 interpolated points per segment so the rib edge
     * follows the panel crossings smoothly instead of with straight facets.
     * Segments between the two halves of a flush-top pair are left straight
     * (they are the flat seats the panels rest on).
     * @param {Array<{x,y,flushPair?}>} pts - Edge points in profile px
     * @returns {Array<{x,y}>} Smoothed edge (original points preserved)
     */
    smoothEdge(pts) {
        if (pts.length < 3) return pts; // nothing to curve through
        const SUBDIV = 3; // interior points inserted per segment
        const catmullRom = (a, b, c, d, t) => {
            const t2 = t * t, t3 = t2 * t;
            return 0.5 * ((2 * b) + (-a + c) * t +
                (2 * a - 5 * b + 4 * c - d) * t2 +
                (-a + 3 * b - 3 * c + d) * t3);
        };
        const out = [pts[0]];
        for (let i = 0; i < pts.length - 1; i++) {
            const p1 = pts[i], p2 = pts[i + 1];
            // Keep flush-top seats perfectly flat
            const isSeat = p1.flushPair !== undefined && p1.flushPair === p2.flushPair;
            if (!isSeat) {
                const p0 = pts[i - 1] || p1;
                const p3 = pts[i + 2] || p2;
                for (let k = 1; k <= SUBDIV; k++) {
                    const t = k / (SUBDIV + 1);
                    out.push({
                        x: catmullRom(p0.x, p1.x, p2.x, p3.x, t),
                        y: catmullRom(p0.y, p1.y, p2.y, p3.y, t)
                    });
                }
            }
            out.push(p2);
        }
        return out;
    }

    /**
     * Build the leg brace slice for one drawn leg (Java parity:
     * GeometryOperations tieToLeg branches + SketchChair.mouseReleased leg
     * slice creation).
     *
     * The rib plane CONTAINS the leg's centre line (normal = leg direction
     * rotated 90 deg in the sketch plane) and spans the chair depth,
     * connecting the mirrored leg pair on the two outer panels. Its profile
     * (in the plane's PROFILE frame: x = along the leg, y = world Z depth,
     * sketch px) is the union of:
     * - an under-seat beam spanning the full depth (Java's top band),
     * - one "leg drop" per outer panel running from the seat underside to
     *   the foot (Java's ASCII art:  |:/  \:| ),
     * - an optional CROSSBRACE between the feet (selection.crossbrace,
     *   with crossbraceWidth / crossbraceBottomOffset in mm ~ px).
     * All parts are unioned into ONE outline (single fabricated part).
     *
     * @param {CrossSliceSelection} selection - LEG-type selection (options)
     * @param {SketchSpline} legSpline - The drawn leg (2-point centre path)
     * @param {number} materialThickness - Plane thickness (mm)
     * @returns {Promise<SlicePlane|null>} The built plane, or null
     */
    async buildLegSlicePlane(selection, legSpline, materialThickness = 5) {
        // Debug record for the leg-brace overlay ('j' key) - always captured
        // (cheap), rendered/logged only while the overlay is enabled
        const debug = {
            legIndex: selection.legIndex ?? 0,
            top: null, foot: null,
            legWidth: null, capAllowance: null,
            xTop: null, xFoot: null,
            planePos: null, profileXAxis: null,
            crossings: [],           // {world:[x,y,z], relX, accepted, planeIndex}
            crossingsPerPanel: [],
            seatX: null, seatFallback: false, extendToTop: false,
            rects: [],               // profile-frame rects BEFORE union
            bail: null               // stage that failed, or null
        };
        (this.legBraceDebug ??= []).push(debug);

        const pts = legSpline?.centrePath?.points;
        if (!pts || pts.length < 2) { debug.bail = 'centre path < 2 points'; return null; }

        let p1 = pts[0];                 // leg top (sketch px) - drawn first
        let p2 = pts[pts.length - 1];    // foot
        let dx = p2.x - p1.x, dy = p2.y - p1.y;
        const lenPx = Math.hypot(dx, dy);
        if (lenPx < 1e-6) { debug.bail = 'zero-length centre line'; return null; }
        let legDir = { x: dx / lenPx, y: dy / lenPx };
        debug.top = { x: p1.x, y: p1.y };
        debug.foot = { x: p2.x, y: p2.y };

        // Java: dir2D = (p1-p2).normalize().rotate(90deg) - the plane
        // contains the leg line and the world Z axis
        const normal = new THREE.Vector3(-legDir.y, legDir.x, 0).normalize();
        const midWorld = new THREE.Vector3(
            (p1.x + p2.x) / 2 * 0.01,
            (p1.y + p2.y) / 2 * 0.01,
            0
        );

        const plane = new SlicePlane({
            position: midWorld,
            normal,
            thickness: materialThickness,
            guide: false,
            selected: false
        });
        plane.tiedToLeg = true;
        plane.isLegSlice = true;
        plane.crossSliceSelection = selection;
        // Must match the frame generateMeshesForPlane renders vertical
        // planes in: local X -> (normal.y, -normal.x, 0), local Y -> world Z.
        // For this normal that X axis IS the leg direction (top -> foot).
        plane.profileXAxis = new THREE.Vector3(normal.y, -normal.x, 0).normalize();

        // Physical (non-guide) panels define the depth span; the outer two
        // own the legs (legs-on-outer-layers rule)
        const physical = (this.basePlanes || []).filter(p => !p.guide);
        if (physical.length < 2) { debug.bail = `only ${physical.length} physical panel(s)`; return null; }
        const zsPx = physical.map(p => p.position.z * 100);
        const zMinPx = Math.min(...zsPx);
        const zMaxPx = Math.max(...zsPx);

        // Leg drawn width (px) sizes the drops (Java measured the leg
        // outline's top width)
        const legWidth = Math.max(10, (legSpline.offsetSize || 10) * 2);
        const halfW = legWidth / 2;

        // Profile x: 0 at the leg midpoint, negative toward the top
        const xTop = -lenPx / 2;
        const xFoot = lenPx / 2;

        // Seat underside along the leg (Java: farthest outline crossing
        // from the top intersect; extendLegSliceToTopOfLeg skips the clip).
        //
        // TOLERANT anchor: the leg's centre LINE does not have to touch the
        // chair profile - a leg whose top END CAP overlaps the seat is
        // visually attached (CAP_LEG extends ~1.5x the top radius ABOVE the
        // top centre point). So crossings are accepted up to the cap extent
        // ABOVE the leg top, and the brace profile extends UP to that
        // crossing (seatX may be < xTop) so it always anchors into the seat.
        // Crossings must still lie in the UPPER half of the leg - the
        // infinite plane can also cross the seat far away from this leg.
        const capAllowance = legWidth; // >= 1.5 * top radius, with margin
        debug.legWidth = legWidth;
        debug.capAllowance = capAllowance;
        debug.xTop = xTop;
        debug.xFoot = xFoot;
        debug.planePos = [plane.position.x, plane.position.y, plane.position.z];
        debug.profileXAxis = [plane.profileXAxis.x, plane.profileXAxis.y, plane.profileXAxis.z];
        debug.extendToTop = !!selection.extendLegSliceToTopOfLeg;

        let seatX = xTop;
        if (!selection.extendLegSliceToTopOfLeg) {
            // Collect all profile crossings on the leg plane once (relX in
            // the AS-DRAWN orientation)
            for (let pi = 0; pi < physical.length; pi++) {
                const bp = physical[pi];
                const inters = bp.getIntersectionWith(plane);
                debug.crossingsPerPanel.push(inters.length);
                for (const inter of inters) {
                    const relX = new THREE.Vector3()
                        .subVectors(inter.world, plane.position)
                        .dot(plane.profileXAxis) * 100;
                    debug.crossings.push({
                        world: [inter.world.x, inter.world.y, inter.world.z],
                        relX, accepted: false, planeIndex: pi
                    });
                }
            }

            // Anchor for the as-drawn orientation: farthest crossing in
            // the window just above the drawn TOP end
            const bestFor = (sign) => {
                let best = null;
                for (const c of debug.crossings) {
                    const rx = sign * c.relX;
                    if (rx > xTop - capAllowance && rx < 0 &&
                        (best === null || rx > best)) {
                        best = rx;
                    }
                }
                return best;
            };

            let best = bestFor(1);
            if (best === null) {
                // AS-DRAWN top end found no seat. The user may have drawn
                // the leg UPWARD (foot first): the attached end is then the
                // LAST centre point, not the first. Try the flipped
                // orientation before giving up.
                const bestFlipped = bestFor(-1);
                if (bestFlipped !== null) {
                    console.log('buildLegSlicePlane: leg drawn foot-first - auto-flipping orientation (seat found at the other end)');
                    debug.flipped = true;
                    [p1, p2] = [p2, p1];
                    legDir = { x: -legDir.x, y: -legDir.y };
                    plane.normal.negate();
                    plane.plane?.setFromNormalAndCoplanarPoint?.(plane.normal, plane.position);
                    plane.profileXAxis.negate();
                    for (const c of debug.crossings) c.relX = -c.relX;
                    debug.top = { x: p1.x, y: p1.y };
                    debug.foot = { x: p2.x, y: p2.y };
                    debug.profileXAxis = [plane.profileXAxis.x, plane.profileXAxis.y, plane.profileXAxis.z];
                    best = bestFlipped;
                }
            }

            if (best !== null) {
                seatX = best;
                // Flag window membership + the winning crossing (overlay:
                // red dots = candidates, green = chosen seat anchor)
                for (const c of debug.crossings) {
                    c.accepted = c.relX > xTop - capAllowance && c.relX < 0;
                }
                for (const c of debug.crossings) {
                    if (c.accepted && c.relX === best) { c.chosen = true; break; }
                }
            } else {
                debug.seatFallback = true;
                debug.bail = debug.crossings.length === 0
                    ? 'no profile crossings at all on the leg plane'
                    : 'crossings exist but none inside the acceptance window (either end)';
                console.warn('buildLegSlicePlane: no seat crossing within the ' +
                    'leg cap extent at either end - brace anchored at the ' +
                    'bare leg top (leg may not be attached to the chair profile)');
            }
        }
        debug.seatX = seatX;

        // ---- Profile -----------------------------------------------------
        // The leg rib is built EXACTLY like a regular cross-slice rib: its
        // plane intersects the chair profile on ALL layers, giving the same
        // contour-hugging panel-intersection profile (outline-clipped edges,
        // caps, smoothing - buildCrossSliceProfile semantics). The ONE
        // difference: on the leg's own layers (the tied OUTER planes) the
        // profile EXTENDS down to the foot - a "leg drop" rect per outer
        // panel is unioned in (plus the optional crossbrace between the two
        // drops). Slots joining rib <-> leg-layer panel sit HALFWAY UP that
        // extension (see generateSlots).
        const rect = (x0, x1, y0, y1) => [
            { x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }
        ];

        // Tell buildCrossSliceProfile to choose each layer's chord nearest the
        // seat (not the leg midpoint), so a stretcher/offset-line drawn between
        // the legs can't hijack the outer layers' band.
        plane.legChordRefX = seatX;
        const contourProfile = await this.buildCrossSliceProfile(plane, selection, legSpline?.centrePath);

        const parts = [];
        if (contourProfile && contourProfile.points?.length >= 3) {
            // Same profile a normal rib gets on this plane (px, x along the
            // leg, y = depth). Sample so smoothing beziers survive the union.
            parts.push({
                outer: sampleRing(contourProfile.points),
                holes: (contourProfile.holes || []).map(h => sampleRing(h)).filter(h => h.length >= 3)
            });
        } else {
            // Detached leg / no chair crossings: fall back to a synthetic
            // under-seat beam so the leg still gets its brace (never fail)
            console.warn('buildLegSlicePlane: contour profile unavailable, using synthetic beam fallback');
            debug.bail = debug.bail || 'contour profile unavailable (synthetic beam fallback)';
            const beamH = Math.max(20, selection.slatHeight || 40); // px
            parts.push({ outer: rect(seatX, Math.min(seatX + beamH, xFoot), zMinPx - halfW, zMaxPx + halfW), holes: [] });
        }

        // Leg drops at the two outer (leg-owning) panels: from the seat
        // underside down to the foot. The drop's width MIRRORS THE LEG'S
        // TAPER: at every position along the leg the extension is exactly
        // as wide as the leg itself (per-point radii via centreOffset), so
        // editing a leg point's width reshapes the rib extension on
        // regeneration. (Java derived the drop width from the leg outline;
        // this generalizes that to the full tapered silhouette.)
        //
        // The drop also overlaps INTO the contour band (seatX is exactly
        // the band's under edge - an exact tangency there is degenerate
        // for the union: the jitter can open a hairline gap, losing the
        // connection and any enclosed crossbrace window).
        const legSamples = (legSpline.sampleCentrePath?.() || [])
            .map(s => ({
                x: new THREE.Vector3(s.x * 0.01, s.y * 0.01, 0)
                    .sub(plane.position).dot(plane.profileXAxis) * 100,
                r: s.r
            }))
            .sort((a, b) => a.x - b.x);
        const rAt = (x) => {
            if (legSamples.length === 0) return halfW;
            if (x <= legSamples[0].x) return legSamples[0].r;
            if (x >= legSamples[legSamples.length - 1].x) return legSamples[legSamples.length - 1].r;
            for (let i = 0; i + 1 < legSamples.length; i++) {
                const a = legSamples[i], b = legSamples[i + 1];
                if (x >= a.x && x <= b.x) {
                    const t = (b.x - a.x) > 1e-9 ? (x - a.x) / (b.x - a.x) : 0;
                    return a.r + (b.r - a.r) * t;
                }
            }
            return halfW;
        };

        // Tapered drop polygon centered on depth zc: upper edge out along
        // the leg, lower edge back; half-width = the leg's LOCAL radius.
        //
        // Each drop anchors its TOP to the seat crossing ON ITS OWN OUTER
        // LAYER, not the global seat anchor. With the chair curve active the
        // middle layers bulge, so their seat crossing marches toward the foot
        // (measured: outer-layer seat at -130px, mid-layer at -66px). The two
        // drops live on the OUTER (leg-owning) layers, whose seat sits much
        // higher up the leg; anchoring them to the global (mid-layer-hijacked)
        // seatX left the drop tops ~60px short of the outer seat, opening the
        // visible gap between the leg and the chair. Per-outer-layer anchoring
        // keeps each drop reaching its own seat regardless of curvature.
        // Both edges of the seat the leg pierces ON ONE LAYER: lo = the far
        // edge (seat top surface), hi = the near edge (seat underside).
        // null when the layer has no chord at all.
        //
        // The edges come from the layer's CHORD through the seat, not from
        // the seat-anchor acceptance window: the window is sized by the LEG's
        // width (capAllowance), so a leg whose top point stops low in the seat
        // - below the seat stroke's centre line - has its seat TOP crossing
        // fall outside it. lo then collapsed onto the underside, the drop
        // started at the seat's bottom edge, and the rib's cap was left to
        // span the seat band alone (the concave cut-in above the leg).
        // Chording instead makes the drop ALWAYS travel to the furthest-most
        // edge of the seat it intersects, and agrees exactly with the contour
        // band, which picks its chord the same way (filterToLocalChord with
        // chordRef = seatX).
        const seatEdgesAtPlane = (planeIdx) => {
            // Ordered along the leg (top -> foot); consecutive pairs are the
            // chords INSIDE the outline
            const rs = debug.crossings
                .filter(c => c.planeIndex === planeIdx)
                .map(c => c.relX)
                .sort((a, b) => a - b);
            let best = null, bestDist = Infinity;
            for (let k = 0; k + 1 < rs.length; k += 2) {
                const lo = rs[k], hi = rs[k + 1];
                const dist = (seatX >= lo && seatX <= hi)
                    ? 0
                    : Math.min(Math.abs(seatX - lo), Math.abs(seatX - hi));
                if (dist < bestDist) { bestDist = dist; best = { lo, hi }; }
            }
            return best;
        };
        const planeIdxForDepth = (zc) => {
            let bi = 0, bd = Infinity;
            for (let i = 0; i < physical.length; i++) {
                const dz = Math.abs(physical[i].position.z * 100 - zc);
                if (dz < bd) { bd = dz; bi = i; }
            }
            return bi;
        };

        // Seat TOP surface as a function of DEPTH. The leg drop straddles its
        // outer panel with the leg's half-width to either side, so its top
        // edge covers depths BETWEEN panels. Pinning that whole top edge to
        // the outer panel's single seat height made the drop's interior side
        // ride ABOVE the seat wherever the seat curves (concave/convex "chair
        // curve"): the flat top poked through the seating surface - an
        // uncomfortable bump on the inner side of the rib.
        //
        // The reference is the RIB's own contour outline (contourProfile,
        // already built above): sampling its top edge at each depth follows
        // the true CURVED seat exactly, so the drop never rides proud of it -
        // sampling the discrete per-layer seat crossings instead only gets the
        // straight chord between layers, which still over/undershoots the
        // curve. The per-layer crossings survive as a fallback for depths
        // outside the contour (beyond the outermost panel) and for the
        // detached-leg synthetic beam where no contour exists.
        const contourPts = (contourProfile && contourProfile.points?.length >= 3)
            ? sampleRing(contourProfile.points) : null;
        const contourTopAtDepth = (y) => {
            if (!contourPts) return null;
            let minx = Infinity;
            for (let i = 0; i < contourPts.length; i++) {
                const a = contourPts[i], b = contourPts[(i + 1) % contourPts.length];
                if ((a.y <= y && b.y >= y) || (b.y <= y && a.y >= y)) {
                    if (Math.abs(b.y - a.y) < 1e-9) { minx = Math.min(minx, a.x, b.x); continue; }
                    minx = Math.min(minx, a.x + (b.x - a.x) * (y - a.y) / (b.y - a.y));
                }
            }
            return minx === Infinity ? null : minx;
        };
        const layerSeat = physical
            .map((bp, i) => ({ y: bp.position.z * 100, lo: seatEdgesAtPlane(i)?.lo }))
            .filter(s => Number.isFinite(s.lo))
            .sort((a, b) => a.y - b.y);
        const seatTopAtDepth = (y) => {
            if (layerSeat.length === 0) return null;
            if (y <= layerSeat[0].y) return layerSeat[0].lo;
            const last = layerSeat[layerSeat.length - 1];
            if (y >= last.y) return last.lo;
            for (let i = 0; i + 1 < layerSeat.length; i++) {
                const a = layerSeat[i], b = layerSeat[i + 1];
                if (y >= a.y && y <= b.y) {
                    const t = (b.y - a.y) > 1e-9 ? (y - a.y) / (b.y - a.y) : 0;
                    return a.lo + (b.lo - a.lo) * t;
                }
            }
            return last.lo;
        };

        const dropAt = (zc) => {
            // The drop spans depth [zc - hw, zc + hw] (hw = leg half-width near
            // the seat), straddling its outer panel at zc. Its TOP edge follows
            // the RIB CONTOUR at each depth so the interior face can't ride
            // proud of a curved seat, tucked 1px INTO the material rather than
            // proud of it so it never protrudes above the seat surface yet
            // still overlaps the contour band solidly (martinez needs the
            // overlap area, not a bare tangency, to weld the parts).
            const useContourTop = !selection.extendLegSliceToTopOfLeg &&
                (contourPts || layerSeat.length > 0);
            const globalTopX = seatX - rAt(seatX); // fallback (extend-to-top / no seat)
            const topXAt = (y) => {
                if (!useContourTop) return globalTopX;
                const c = contourTopAtDepth(y);
                if (Number.isFinite(c)) return c + 1; // a hair INTO the seat, never proud of it
                const s = seatTopAtDepth(y);
                return (Number.isFinite(s) ? s : globalTopX) - 1; // outside the contour: proud
            };

            const hw = rAt(seatX); // drop half-depth near the seat
            const yPlus = zc + hw, yMinus = zc - hw;
            const xTopPlus = topXAt(yPlus);
            const xTopMinus = topXAt(yMinus);

            const STEPS = 8;
            // +depth face: from its (seat-following) top corner down to the foot
            const upper = [];
            for (let i = 0; i <= STEPS; i++) {
                const x = xTopPlus + (xFoot - xTopPlus) * i / STEPS;
                upper.push({ x, y: zc + rAt(x) });
            }
            // -depth face, walked back from the foot to its top corner
            const lower = [];
            for (let i = STEPS; i >= 0; i--) {
                const x = xTopMinus + (xFoot - xTopMinus) * i / STEPS;
                lower.push({ x, y: zc - rAt(x) });
            }
            // Close the TOP across depth, following the seat (so the panel
            // depth passes through its own seat top, not a straight chord)
            const topEdge = [];
            const TSTEPS = 4;
            for (let i = 1; i < TSTEPS; i++) {
                const y = yMinus + (yPlus - yMinus) * i / TSTEPS;
                topEdge.push({ x: topXAt(y), y });
            }
            return { outer: [...upper, ...lower, ...topEdge], holes: [] };
        };
        parts.push(dropAt(zMinPx));
        parts.push(dropAt(zMaxPx));

        if (selection.crossbrace) {
            const braceW = Math.max(5, selection.crossbraceWidth ?? 40);          // px (1px = 1mm)
            const braceOff = Math.max(0, selection.crossbraceBottomOffset ?? 20); // px from the feet
            const bx1 = xFoot - braceOff;
            const bx0 = bx1 - braceW;
            if (bx1 > seatX) {
                // Span out to the LOCAL leg width at the brace position so
                // the brace fully engages the tapered drops
                const rBrace = rAt(bx1);
                parts.push({ outer: rect(Math.max(bx0, seatX), bx1, zMinPx - rBrace, zMaxPx + rBrace), holes: [] });
            }
        }

        debug.rects = parts.map(p => p.outer.map(pt => ({ x: pt.x, y: pt.y })));

        const unioned = unionShapes(parts);
        if (unioned.length === 0) { debug.bail = 'unionShapes returned empty'; return null; }

        // Leg extension metadata for the slot pass: rib <-> leg-layer slots
        // sit halfway up the extension (seat underside -> foot). Use the
        // OUTER-layer seat anchor (where the leg-owning panels and their slots
        // actually are), not the global mid-layer seatX - otherwise curvature
        // drags the slot midpoint toward the foot along with the old drop top.
        const outerEdge = seatEdgesAtPlane(planeIdxForDepth(zMinPx))
            ?? seatEdgesAtPlane(planeIdxForDepth(zMaxPx));
        const outerSeatX = outerEdge ? outerEdge.hi : seatX;
        plane.legExtension = { seatX: outerSeatX, xFoot, halfW };
        // Keep the largest connected region (all parts overlap the drops,
        // so normally there is exactly one)
        const areaOf = (poly) => {
            let a = 0;
            const ring = poly.outer;
            for (let i = 0; i < ring.length; i++) {
                const p = ring[i], q = ring[(i + 1) % ring.length];
                a += p.x * q.y - q.x * p.y;
            }
            return Math.abs(a / 2);
        };
        unioned.sort((a, b) => areaOf(b) - areaOf(a));
        const profilePoly = unioned[0];

        const { SketchPath } = await import('../sketch/SketchPath.js');
        const { SketchPoint } = await import('../sketch/SketchPoint.js');
        const profile = new SketchPath(null);
        for (const p of profilePoly.outer) {
            profile.add(new SketchPoint(p.x, p.y));
        }
        profile.setClosed(true);
        profile.holes = (profilePoly.holes || []).map(hole =>
            hole.map(p => new SketchPoint(p.x, p.y)));

        plane.sketch = {
            closedShapes: [profile],
            splines: [],
            paths: [],
            legShapes: []
        };

        return plane;
    }

    async buildCrossSliceProfile(crossSlicePlane, crossSliceSelection, guidePath) {
        const { SketchPath } = await import('../sketch/SketchPath.js');
        const { SketchPoint } = await import('../sketch/SketchPoint.js');

        // Collect all intersection points with other planes
        const allIntersections = [];

        // 🐛 DEBUG: Draw the intersection line in 3D space
        if (DEBUG_INTERSECTIONS) {
            // The intersection line is PERPENDICULAR to the cross-slice normal
            // Match the mathematical cross product: (0,0,1) × (nx,ny,0) = (-ny, nx, 0)
            const lineLength = 2.0; // 2 units = 200cm visible line
            const perpendicular = new THREE.Vector3(-crossSlicePlane.normal.y, crossSlicePlane.normal.x, 0).normalize();
            console.log(`🐛 Yellow line calculation:`);
            console.log(`   → Cross-slice normal: (${crossSlicePlane.normal.x.toFixed(3)}, ${crossSlicePlane.normal.y.toFixed(3)}, ${crossSlicePlane.normal.z.toFixed(3)})`);
            console.log(`   → Perpendicular direction: (${perpendicular.x.toFixed(3)}, ${perpendicular.y.toFixed(3)}, ${perpendicular.z.toFixed(3)})`);
            const lineStart3D = crossSlicePlane.position.clone().addScaledVector(perpendicular, lineLength);
            const lineEnd3D = crossSlicePlane.position.clone().addScaledVector(perpendicular, -lineLength);
            console.log(`   → Line from: (${lineStart3D.x.toFixed(3)}, ${lineStart3D.y.toFixed(3)}, ${lineStart3D.z.toFixed(3)})`);
            console.log(`   → Line to:   (${lineEnd3D.x.toFixed(3)}, ${lineEnd3D.y.toFixed(3)}, ${lineEnd3D.z.toFixed(3)})`);
            this.addDebugLine(lineStart3D, lineEnd3D, 0xFFFF00); // Yellow line - intersection direction
        }

        // NOTE: We ONLY use base plane intersections for cross-slice profile building
        // The primary plane intersections (green dots) are incorrect for spanning depth
        // We need base plane intersections at all Z levels (cyan dots) to build the correct profile

        // "Through all" off (the default): the profile only spans the shape
        // that generated this slice group, not every shape on the plane
        const clipShapeIndex = (crossSliceSelection && crossSliceSelection.cropToCurrentShape !== false)
            ? (crossSliceSelection?.shapeIndex ?? 0)
            : null;

        // A leg brace is through-all (spans every shape on a layer), so on
        // the outer layers its plane also crosses any stretcher/offset-line
        // drawn between the legs. filterToLocalChord keeps the ONE chord
        // nearest its reference; using the leg midpoint (plane.position) a
        // foot-height stretcher can sit nearer than the seat, so that layer's
        // band jumps to the stretcher and the seat rib loses the outer layer.
        // Anchor the chord choice at the SEAT instead so every layer agrees.
        let chordRef = null;
        if (crossSlicePlane.isLegSlice && Number.isFinite(crossSlicePlane.legChordRefX)) {
            chordRef = crossSlicePlane.position.clone()
                .addScaledVector(crossSlicePlane.profileXAxis, crossSlicePlane.legChordRefX / 100);
        }

        // Check intersections with base planes (different Z levels)
        console.log(`      → Checking ${this.basePlanes.length} base planes at different Z levels...`);
        for (const basePlane of this.basePlanes) {
            if (!basePlane.sketch || !basePlane.sketch.closedShapes) continue;

            // Get intersections from base plane to cross-slice, clipped to
            // the chord local to this slice's position on the guide path
            const intersections = this.filterToLocalChord(
                basePlane.getIntersectionWith(crossSlicePlane, clipShapeIndex),
                crossSlicePlane, null, chordRef
            );

            for (const intersection of intersections) {
                allIntersections.push({
                    ...intersection,
                    sourcePlane: basePlane
                });

                // 🐛 DEBUG: Add cyan sphere at base plane intersection point
                if (DEBUG_INTERSECTIONS && intersection.world) {
                    this.addDebugSphere(intersection.world, 0x00FFFF, 0.10); // Cyan sphere, 10cm radius
                }
            }
        }

        console.log(`      → Found ${allIntersections.length} intersection points for cross-slice`);

        if (allIntersections.length === 0) {
            return null;
        }

        // Project intersection points onto cross-slice 2D coordinate system
        // X axis = direction that separates the two intersection points (sketch width)
        // Y axis = along world Z (depth through chair)
        console.log(`🔵 PROJECTION: Converting ${allIntersections.length} world points to cross-slice local coords`);

        // Profile X axis must be DETERMINISTIC and match the frame the mesh
        // is rendered in (mesh local X maps to world angle normalAngle-90deg,
        // see generateMeshesForPlane). It was previously derived from
        // whichever same-Z intersection pair was found first - a sign
        // ambiguity that mirrored ASYMMETRIC profiles about the spawn point.
        // Symmetric mid-arm ribs are mirror-invariant so only ribs near
        // bends visibly protruded past the stroke edge.
        const crossSliceXAxis = new THREE.Vector3(
            crossSlicePlane.normal.y,
            -crossSlicePlane.normal.x,
            0
        ).normalize();

        // Cross-slice Y axis = world Z axis (depth through chair)
        const crossSliceYAxis = new THREE.Vector3(0, 0, 1);

        // Store the profile frame on the plane so slots (and anything else that
        // must line up with the profile) can be expressed in the same 2D space.
        // See SlicePlane.getProfilePos / getProfileWorldPos.
        crossSlicePlane.profileXAxis = crossSliceXAxis.clone();

        console.log(`🔵 Cross-slice X axis: (${crossSliceXAxis.x.toFixed(3)}, ${crossSliceXAxis.y.toFixed(3)}, ${crossSliceXAxis.z.toFixed(3)})`);
        console.log(`🔵 Cross-slice Y axis: (${crossSliceYAxis.x.toFixed(3)}, ${crossSliceYAxis.y.toFixed(3)}, ${crossSliceYAxis.z.toFixed(3)})`);

        const crossSliceLocalPoints = allIntersections.map((intersection, idx) => {
            // Vector from cross-slice center to intersection point
            const relativePos = new THREE.Vector3()
                .subVectors(intersection.world, crossSlicePlane.position);

            // Project onto X axis (distance along sketch width) - KEEP IN WORLD UNITS
            const localX = relativePos.dot(crossSliceXAxis);

            // Project onto Y axis (world Z depth) - KEEP IN WORLD UNITS
            const localY = relativePos.dot(crossSliceYAxis);

            // SWAP X and Y because the cross-slice plane is rotated 90° (using tangent as normal)
            // When rendered on the wrong plane, X becomes Y and Y becomes X
            const swappedX = localY;  // Depth becomes X on the rotated plane
            const swappedY = localX;  // Width becomes Y on the rotated plane

            console.log(`🔵 Point ${idx + 1}: World (${intersection.world.x.toFixed(3)}, ${intersection.world.y.toFixed(3)}, ${intersection.world.z.toFixed(3)}) → Local (${localX.toFixed(4)}, ${localY.toFixed(4)}) → Swapped (${swappedX.toFixed(4)}, ${swappedY.toFixed(4)}) world units`);
            return {
                local: { x: swappedX, y: swappedY },
                world: intersection.world,
                sourcePlane: intersection.sourcePlane
            };
        });

        // Sort by world Z coordinate (depth through the chair)
        // This ensures we build the profile from first base plane to last
        crossSliceLocalPoints.sort((a, b) => a.world.z - b.world.z);

        console.log(`🔵 AFTER SORTING BY Z:`);
        console.log(`      → World Z range: ${crossSliceLocalPoints[0].world.z.toFixed(3)} to ${crossSliceLocalPoints[crossSliceLocalPoints.length - 1].world.z.toFixed(3)}`);
        console.log(`      → Local X range (swapped from depth): ${Math.min(...crossSliceLocalPoints.map(p => p.local.x)).toFixed(3)} to ${Math.max(...crossSliceLocalPoints.map(p => p.local.x)).toFixed(3)} world units`);
        console.log(`      → Local Y range (swapped from width): ${Math.min(...crossSliceLocalPoints.map(p => p.local.y)).toFixed(3)} to ${Math.max(...crossSliceLocalPoints.map(p => p.local.y)).toFixed(3)} world units`);

        const worldDepth = Math.abs(crossSliceLocalPoints[0].world.z - crossSliceLocalPoints[crossSliceLocalPoints.length - 1].world.z);
        console.log(`      → World depth: ${worldDepth.toFixed(3)} world units (4 units = 4cm)`);
        console.log(`      → Profile X span: ${(Math.max(...crossSliceLocalPoints.map(p => p.local.x)) - Math.min(...crossSliceLocalPoints.map(p => p.local.x))).toFixed(3)} world units`);

        // Build profile using actual intersection points
        // After swapping: local.x = depth (spans base planes), local.y = width (sketch edges)
        const profile = new SketchPath(null);

        // IMPORTANT: SketchPath expects coordinates in PIXELS (100 pixels = 1 world unit)
        const scale = 100; // pixels per world unit

        // Group points by Z level (depth)
        const pointsByZ = new Map();
        for (const point of crossSliceLocalPoints) {
            const zKey = point.local.x.toFixed(6); // Use depth as key
            if (!pointsByZ.has(zKey)) {
                pointsByZ.set(zKey, []);
            }
            pointsByZ.get(zKey).push(point);
        }

        console.log(`      → Grouped into ${pointsByZ.size} depth levels`);

        // Build profile by tracing left edge forward, then right edge backward
        const leftEdge = [];
        const rightEdge = [];

        // Sort each Z level's points by width (local.y) to identify left/right edges
        let maxChordPx = 0;
        for (const [zKey, points] of pointsByZ.entries()) {
            points.sort((a, b) => a.local.y - b.local.y); // Sort by width
            leftEdge.push(points[0]);  // Leftmost point
            rightEdge.unshift(points[points.length - 1]); // Rightmost point (add to front for reverse order)
            maxChordPx = Math.max(maxChordPx,
                (points[points.length - 1].local.y - points[0].local.y) * 100);
        }

        // Widest single-layer chord (sketch px). The oblique/sliver culls
        // must use THIS, not the profile bbox: with the chair curve active
        // the layers fan apart radially, so a perfectly healthy rib's bbox
        // is stroke-width + fan-spread wide - culling on the bbox dropped
        // runs of ribs along the curved section (the visible gaps).
        crossSlicePlane.profileMaxChordPx = maxChordPx;

        console.log(`      → Left edge: ${leftEdge.length} points, Right edge: ${rightEdge.length} points`);

        // Convert both edges to pixel-space ring points
        // (X = width across the stroke, Y = depth across the panels)
        const toPx = (p) => ({
            x: p.local.y * scale,
            y: p.local.x * scale,
            thickness: p.sourcePlane?.thickness ?? 5
        });
        let leftPts = leftEdge.map(toPx);    // walked forward (increasing depth)
        let rightPts = rightEdge.map(toPx);  // walked backward (decreasing depth)

        // ---- Flush Tops option (Java: GeometryOperations.buildSlicePath
        // ~1246-1296) - split every panel-crossing point into a pLeft/pRight
        // pair offset by +-(panel thickness)/2 ALONG the edge (depth)
        // direction, so each crossing sits on a flat seat of the rib edge.
        if (crossSliceSelection?.generateFlushTops) {
            leftPts = this.splitEdgeForFlushTops(leftPts);
            rightPts = this.splitEdgeForFlushTops(rightPts);
        }

        // ---- Smooth option (Java: bottomLine.smoothLeft / topLine.
        // smoothRight bezier pass) - the rib edges follow the panel crossings
        // smoothly: sample a Catmull-Rom curve through the crossing points
        // (~3 points inserted per segment) instead of straight segments.
        // Flush-top seat segments are kept perfectly flat.
        if (crossSliceSelection?.smooth) {
            leftPts = this.smoothEdge(leftPts);
            rightPts = this.smoothEdge(rightPts);
        }

        // ---- Slice cap styling -----------------------------------------
        // The bare profile spans EXACTLY between the outermost panel
        // crossings. All cap styles except CAP_INSIDE shape the rib's two
        // ends BEYOND the outer panels (Java: buildSlicePath capping).
        const capType = crossSliceSelection?.capType ?? CapType.CAP_CURVE;

        // Cap length: outer panel thickness + 10px margin (sketch units)
        // Cap length: user-set (selection.capLength, sketch px = mm) or the
        // default outer panel thickness + 10px margin
        const capLengthAt = (endPoint) => {
            const custom = Number(crossSliceSelection?.capLength);
            if (Number.isFinite(custom) && custom >= 0) return custom;
            return (endPoint?.sourcePlane?.thickness ?? 5) + 10;
        };

        // CAP CORNER RADIUS (percent, default 100 = classic behavior).
        // Scales the round-square corner arcs and the winged quarter
        // circle; always clamped to the geometric maximum (cap length /
        // half width), and 0% degrades gracefully to square corners.
        const capRadiusPct = (() => {
            const v = Number(crossSliceSelection?.capRadius);
            return Number.isFinite(v) && v >= 0 ? v / 100 : 1;
        })();

        // CURVED CAPS (default ON): continue the rib profile's local
        // curvature through the cap instead of extending straight - the
        // cap points are sheared along the edge's end direction (dx/dy at
        // the profile end), so on a chair-curve-bent rib the caps follow
        // the bend rather than sticking out flat.
        const curvedCaps = crossSliceSelection?.curvedCaps !== false;
        const endSlope = (pA, pB) => {
            if (!pA || !pB) return 0;
            const dy = pA.y - pB.y;
            if (Math.abs(dy) < 1e-6) return 0;
            // Clamp: near-flat edge ends must not shear caps sideways wildly
            return Math.max(-2, Math.min(2, (pA.x - pB.x) / dy));
        };

        /**
         * Generate the intermediate cap points walked from ring point `a` to
         * ring point `b` around one end of the profile.
         * @param {{x,y}} a - End corner the ring arrives at
         * @param {{x,y}} b - End corner the ring leaves from
         * @param {number} dir - +1 at the max-depth end, -1 at the min-depth end
         * @param {number} capLen - How far the cap extends past the panel (px)
         * @returns {Array<{x,y}>} Cap points (empty for CAP_INSIDE)
         */
        const buildCapPoints = (a, b, dir, capLen, slope = 0) => {
            const pts = [];
            const yEnd = (a.y + b.y) / 2;

            if (capType === CapType.CAP_BUTT) {
                // Straight extension with square corners
                pts.push({ x: a.x, y: yEnd + dir * capLen });
                pts.push({ x: b.x, y: yEnd + dir * capLen });
            } else if (capType === CapType.CAP_ROUND_SQUARE) {
                // Straight extension with rounded corners (small arcs).
                // capRadius scales the 8px base radius; the geometric max
                // (cap length / half cap width) always wins.
                const yExt = yEnd + dir * capLen;
                const s = Math.sign(b.x - a.x) || 1;
                const rMax = Math.min(capLen, Math.abs(b.x - a.x) / 2);
                const r = Math.min(8 * capRadiusPct, rMax);
                if (r < 0.5) {
                    // Degenerate radius: plain square corners
                    pts.push({ x: a.x, y: yExt });
                    pts.push({ x: b.x, y: yExt });
                    if (curvedCaps && slope) {
                        for (const p of pts) p.x += slope * (p.y - yEnd);
                    }
                    return pts;
                }
                const STEPS = 3; // 4 points per corner arc
                // Corner nearest `a`: side edge -> outer edge
                const c1 = { x: a.x + s * r, y: yExt - dir * r };
                for (let k = 0; k <= STEPS; k++) {
                    const th = (k / STEPS) * Math.PI / 2;
                    pts.push({
                        x: c1.x - s * r * Math.cos(th),
                        y: c1.y + dir * r * Math.sin(th)
                    });
                }
                // Corner nearest `b`: outer edge -> side edge
                const c2 = { x: b.x - s * r, y: yExt - dir * r };
                for (let k = 0; k <= STEPS; k++) {
                    const th = (k / STEPS) * Math.PI / 2;
                    pts.push({
                        x: c2.x + s * r * Math.sin(th),
                        y: c2.y + dir * r * Math.cos(th)
                    });
                }
            } else if (capType === CapType.CAP_CURVE) {
                // Winged: extended end with a quarter circle on one corner
                // and a square corner on the other. Both rounded corners
                // sit on the SAME edge of the rib (the +X side of the
                // profile), so the two wings share an edge - mirror
                // symmetry, matching the original chairs.
                // The EXTENSION length is fixed (capLen-derived); capRadius
                // scales only the corner's quarter-circle radius within it:
                // 100% = the classic full-extension arc, 0% = square corner.
                const ext = Math.min(capLen + 15, Math.abs(b.x - a.x) * 0.9);
                const r = Math.max(0, Math.min(ext, ext * capRadiusPct));
                const yExt = yEnd + dir * ext;
                // Round whichever end of this cap has the larger x
                const roundB = b.x >= a.x;
                const sq = roundB ? a : b;   // square-corner side
                const rd = roundB ? b : a;   // rounded-corner side (+X)
                const sgn = Math.sign(rd.x - sq.x) || 1;
                const STEPS = 6;

                // Arc centre sits r inside both the outer edge and the side
                // (r === ext reproduces the classic full quarter circle)
                const cx = rd.x - sgn * r;
                const cy = yExt - dir * r;

                const arc = [];
                if (r > 0.5) {
                    for (let k = 0; k <= STEPS; k++) {
                        const th = (k / STEPS) * Math.PI / 2;
                        arc.push({
                            x: cx + sgn * r * Math.sin(th),
                            y: cy + dir * r * Math.cos(th)
                        });
                    }
                } else {
                    arc.push({ x: rd.x, y: yExt }); // square corner
                }

                if (roundB) {
                    // Walk a -> b: square corner first, then arc (top -> side)
                    pts.push({ x: sq.x, y: yExt });
                    for (const q of arc) pts.push(q);
                } else {
                    // Walk a -> b: arc first (side -> top), then square corner
                    for (let k = arc.length - 1; k >= 0; k--) pts.push(arc[k]);
                    pts.push({ x: sq.x, y: yExt });
                }
            }
            // CAP_INSIDE: flush with the outer panels - no extra points

            // Curved continuation: shear the cap along the profile's local
            // end direction (x += slope * extension). Zero at the cap base
            // (y = yEnd) so the cap stays continuous with the rib edges.
            if (curvedCaps && slope) {
                for (const p of pts) {
                    p.x += slope * (p.y - yEnd);
                }
            }
            return pts;
        };

        // Assemble the ring: left edge forward, cap at the max-depth end,
        // right edge backward, cap at the min-depth end (closing the loop)
        const ring = [...leftPts];
        // Mean edge direction at each profile end (left edge walks forward,
        // right edge walks backward) - drives the curved-cap shear
        const maxEndSlope = (endSlope(leftPts[leftPts.length - 1], leftPts[leftPts.length - 2]) +
            endSlope(rightPts[0], rightPts[1])) / 2;
        const minEndSlope = (endSlope(leftPts[0], leftPts[1]) +
            endSlope(rightPts[rightPts.length - 1], rightPts[rightPts.length - 2])) / 2;
        ring.push(...buildCapPoints(
            leftPts[leftPts.length - 1], rightPts[0],
            +1, capLengthAt(leftEdge[leftEdge.length - 1]), maxEndSlope
        ));
        ring.push(...rightPts);
        ring.push(...buildCapPoints(
            rightPts[rightPts.length - 1], leftPts[0],
            -1, capLengthAt(leftEdge[0]), minEndSlope
        ));

        for (const pt of ring) {
            profile.add(new SketchPoint(pt.x, pt.y));
        }

        profile.setClosed(true);

        const minDepth = leftEdge[0].local.x;
        const maxDepth = leftEdge[leftEdge.length - 1].local.x;
        const minWidth = Math.min(...crossSliceLocalPoints.map(p => p.local.y));
        const maxWidth = Math.max(...crossSliceLocalPoints.map(p => p.local.y));

        console.log(`      → Profile depth (Y): ${minDepth.toFixed(4)} to ${maxDepth.toFixed(4)} = ${((maxDepth - minDepth) * 100).toFixed(2)}mm`);
        console.log(`      → Profile width (X): ${minWidth.toFixed(4)} to ${maxWidth.toFixed(4)} = ${((maxWidth - minWidth) * 100).toFixed(2)}mm`);

        return profile;
    }

    /**
     * Create a fallback rectangular profile when intersection-based generation fails
     * @param {number} width - Profile width in mm
     * @param {number} height - Profile height in mm
     * @returns {SketchPath} Rectangular profile
     */
    async createFallbackProfile(width, height) {
        const { SketchPath } = await import('../sketch/SketchPath.js');
        const { SketchPoint } = await import('../sketch/SketchPoint.js');

        const halfWidth = width / 2;
        const halfHeight = height / 2;

        const rect = new SketchPath(null);
        rect.add(new SketchPoint(-halfWidth, -halfHeight));
        rect.add(new SketchPoint(halfWidth, -halfHeight));
        rect.add(new SketchPoint(halfWidth, halfHeight));
        rect.add(new SketchPoint(-halfWidth, halfHeight));
        rect.setClosed(true);

        return rect;
    }

    /**
     * Generate slat slices (horizontal supports with finger joints)
     * @param {CrossSliceSelection} crossSliceSelection - Slat configuration
     * @returns {Array<SlicePlane>} Generated slat planes
     */
    generateSlatSlices(crossSliceSelection) {
        if (!crossSliceSelection.path || !crossSliceSelection.plane) {
            console.warn('CrossSliceSelection missing path or plane');
            return [];
        }

        console.log('Generating slat slices...');

        // Use GeometryOperations to generate slat planes
        const slatPlanes = GeometryOperations.generateSlatSlices(
            crossSliceSelection.path,
            crossSliceSelection.plane,
            crossSliceSelection
        );

        // Add slat planes to collection
        slatPlanes.forEach(plane => {
            plane.crossSliceSelection = crossSliceSelection;
            this.addPlane(plane);
        });

        crossSliceSelection.planes = slatPlanes;

        console.log(`Generated ${slatPlanes.length} slat planes`);

        return slatPlanes;
    }

    /**
     * Get all slots
     * @returns {Array<SliceSlot>} Array of slots
     */
    getSlots() {
        return this.slots;
    }

    /**
     * Get slots for a specific plane
     * @param {SlicePlane} plane - Plane to get slots for
     * @returns {Array<SliceSlot>} Array of slots on this plane
     */
    getSlotsForPlane(plane) {
        return this.slots.filter(slot => slot.plane === plane);
    }

    /**
     * Create a simple chair design with intersecting planes
     * Demo/example method
     * @returns {Array<SlicePlane>} Array of created planes
     */
    createExampleChair() {
        this.planes = [];

        // Create vertical plane (seat back)
        const backPlane = new SlicePlane({
            position: new THREE.Vector3(0, 0, 0),
            normal: new THREE.Vector3(1, 0, 0), // YZ plane
            thickness: 5.0
        });

        // Create horizontal plane (seat)
        const seatPlane = new SlicePlane({
            position: new THREE.Vector3(0, 0, 0),
            normal: new THREE.Vector3(0, 1, 0), // XZ plane
            thickness: 5.0
        });

        this.addPlane(backPlane);
        this.addPlane(seatPlane);

        console.log('Created example chair with 2 intersecting planes');

        return this.planes;
    }

    /**
     * Get all planes
     * @returns {Array<SlicePlane>} Array of planes
     */
    getPlanes() {
        return this.planes;
    }

    /**
     * Clear all planes (and everything derived from them)
     */
    clear() {
        this.planes = [];
        this.selectedPlane = null;
        this.intersections.clear();
        this.slots = [];
        this.basePlanes.forEach(p => p.slots && p.slots.clear());
    }

    /**
     * Update all planes
     */
    update() {
        this.planes.forEach(plane => plane.update());
        this.updateIntersections();
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            planes: this.planes.map(p => p.toJSON()),
            settings: {
                autoGenerateSlots: this.autoGenerateSlots,
                slotWidth: this.slotWidth
            }
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     * @returns {SlicePlaneManager} New manager instance
     */
    static fromJSON(data) {
        const manager = new SlicePlaneManager();

        if (data.settings) {
            manager.autoGenerateSlots = data.settings.autoGenerateSlots;
            manager.slotWidth = data.settings.slotWidth;
        }

        if (data.planes) {
            data.planes.forEach(planeData => {
                const plane = SlicePlane.fromJSON(planeData);
                manager.addPlane(plane);
            });
        }

        return manager;
    }

    /**
     * Get statistics
     * @returns {Object} Stats
     */
    getStats() {
        return {
            planeCount: this.planes.length,
            intersectionCount: this.intersections.size,
            selectedPlane: this.selectedPlane ? this.selectedPlane.id : null
        };
    }

    /**
     * Add debug sphere at a 3D position for visualization
     * @param {THREE.Vector3} position - World position
     * @param {number} color - Color hex (e.g. 0xFF0000)
     * @param {number} radius - Sphere radius in world units
     */
    addDebugSphere(position, color = 0xFF0000, radius = 0.1) {
        if (!this.sceneManager) {
            console.warn('🐛 DEBUG: No scene manager available for debug spheres');
            return;
        }

        const geometry = new THREE.SphereGeometry(radius, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.8
        });
        const sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(position);

        this.sceneManager.add(sphere);
        this.debugSpheres.push(sphere);

        console.log(`🐛 DEBUG SPHERE: Added at (${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)}), color: 0x${color.toString(16).padStart(6, '0')}`);
    }

    /**
     * Add debug line in 3D space for visualization
     * @param {THREE.Vector3} start - Start position
     * @param {THREE.Vector3} end - End position
     * @param {number} color - Color hex (e.g. 0x00FFFF)
     */
    addDebugLine(start, end, color = 0x00FFFF) {
        if (!this.sceneManager) {
            console.warn('🐛 DEBUG: No scene manager available for debug lines');
            return;
        }

        const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
        const material = new THREE.LineBasicMaterial({
            color: color,
            linewidth: 3,
            transparent: true,
            opacity: 0.8
        });
        const line = new THREE.Line(geometry, material);

        this.sceneManager.add(line);
        this.debugSpheres.push(line); // Reuse debugSpheres array for cleanup

        console.log(`🐛 DEBUG LINE: Added from (${start.x.toFixed(3)}, ${start.y.toFixed(3)}, ${start.z.toFixed(3)}) to (${end.x.toFixed(3)}, ${end.y.toFixed(3)}, ${end.z.toFixed(3)}), color: 0x${color.toString(16).padStart(6, '0')}`);
    }

    /**
     * Clear all debug spheres from the scene
     */
    clearDebugSpheres() {
        if (!this.sceneManager) return;

        this.debugSpheres.forEach(sphere => {
            this.sceneManager.remove(sphere);
            sphere.geometry.dispose();
            sphere.material.dispose();
        });
        this.debugSpheres = [];

        console.log('🐛 DEBUG: Cleared all debug spheres');
    }
}
